import { hasUpdateAvailable } from "../../utils/AnalyticsFilterUtils/analyticsDashboards";

export const ANALYTICS_STATE_LOADED = "PORTFOLIO_ANALYTICS_STATE_LOADED";
export const ANALYTICS_FILTERS_CHANGED = "PORTFOLIO_ANALYTICS_FILTERS_CHANGED";
export const ANALYTICS_SCENARIO_PROJECTS_LOADED = "PORTFOLIO_ANALYTICS_SCENARIO_PROJECTS_LOADED";
export const ANALYTICS_CHART_OPTION_CHANGED = "PORTFOLIO_ANALYTICS_CHART_OPTION_CHANGED";
export const ANALYTICS_SERIES_STYLE_CHANGED = "PORTFOLIO_ANALYTICS_SERIES_STYLE_CHANGED";
export const ANALYTICS_EDIT_STARTED = "PORTFOLIO_ANALYTICS_EDIT_STARTED";
export const ANALYTICS_DRAFT_LAYOUT_CHANGED = "PORTFOLIO_ANALYTICS_DRAFT_LAYOUT_CHANGED";
export const ANALYTICS_DRAFT_PANEL_TOGGLED = "PORTFOLIO_ANALYTICS_DRAFT_PANEL_TOGGLED";
export const ANALYTICS_EDIT_CANCELLED = "PORTFOLIO_ANALYTICS_EDIT_CANCELLED";
export const ANALYTICS_VIEW_SAVING = "PORTFOLIO_ANALYTICS_VIEW_SAVING";
export const ANALYTICS_VIEW_SAVED = "PORTFOLIO_ANALYTICS_VIEW_SAVED";
export const ANALYTICS_VIEW_SAVE_FAILED = "PORTFOLIO_ANALYTICS_VIEW_SAVE_FAILED";
export const ANALYTICS_DASHBOARD_SWITCHED = "PORTFOLIO_ANALYTICS_DASHBOARD_SWITCHED";
export const ANALYTICS_DASHBOARDS_CHANGED = "PORTFOLIO_ANALYTICS_DASHBOARDS_CHANGED";
export const ANALYTICS_DOCUMENT_SAVED = "PORTFOLIO_ANALYTICS_DOCUMENT_SAVED";
export const ANALYTICS_FOLLOWERS_LOADED = "PORTFOLIO_ANALYTICS_FOLLOWERS_LOADED";
export const ANALYTICS_NAME_CHANGED = "PORTFOLIO_ANALYTICS_NAME_CHANGED";
export const ANALYTICS_PUBLISHED_WRITE_ASKED = "PORTFOLIO_ANALYTICS_PUBLISHED_WRITE_ASKED";
export const ANALYTICS_PUBLISHED_WRITE_ACKNOWLEDGED = "PORTFOLIO_ANALYTICS_PUBLISHED_WRITE_ACKNOWLEDGED";
export const ANALYTICS_PUBLISHED_WRITE_DECLINED = "PORTFOLIO_ANALYTICS_PUBLISHED_WRITE_DECLINED";

/**
 * One document per (scope, typology) — `docKey` of analyticsDashboards — so the portfolio's
 * Resource & capacity, its Roadmap and a project's Monitoring metrics live side by side.
 */
export const EMPTY_DOC = Object.freeze({
  scope: null,
  typology: null,
  // Content of the dashboard the user is on (its copy, or the published row they follow).
  name: "",
  // [{ target: "PROJECT" | "SCENARIO" | "FIELD", attributeId, values: string[] }]
  filters: [],
  // Panel geometry (null = the default layout) and the ids of the hidden panels.
  layout: null,
  hidden: [],
  // Per panel: { chartType, valueField, columnField, groupBy, filters }
  chartOptions: {},
  // Per Group by field key: [{ valueId, rank, colorToken, hidden }] (SPEC-0060 §4.4 point 7)
  seriesStyles: {},
  // Publisher's objective per panel: { [panelId]: { en, fr } }
  purpose: {},
  source: undefined,
  previous: undefined,
  // The dashboards of the scope: published rows (the standard always listed) and the user's copies.
  published: [],
  mine: [],
  pointer: null,
  // The dashboard on screen: { dashboardKey, follows, published, builtin, version, updatedAt, updatedBy }
  current: null,
  updateAvailable: false,
  followers: null,
  // SET-06 point 6: a publisher following the published row is asked before the first write
  // (`publishedWriteAsked` shows the dialog); once confirmed the row takes every later change.
  publishedWriteAsked: false,
  publishedWriteAcknowledged: false,
  // Edit mode (Customize): changes live in `draft` until Save; Cancel or leaving drops them.
  editing: false,
  draft: null, // { layout, hidden }
  saving: false,
  saveError: false,
  // Bumped when the grid must be laid out again from the saved view (Cancel, Reset, switch).
  viewEpoch: 0,
  loaded: false,
});

export const initialState = {
  docs: {},
  // scenario_id → project_id, to apply a project-attribute filter to usage rows
  scenarioProjectMap: {},
};

/** The document of one (scope, typology), or the empty one so callers never null-check. */
export const selectDoc = (state, docKey) => state.portfolioAnalytics?.docs?.[docKey] || EMPTY_DOC;

const entryMeta = ({ state: _content, ...meta }) => meta;

const contentOf = (entry) => {
  const c = entry?.state || {};
  return {
    name: c.name || "",
    filters: c.filters || [],
    layout: c.layout || null,
    hidden: c.hidden || [],
    chartOptions: c.chartOptions || {},
    seriesStyles: c.seriesStyles || {},
    purpose: c.purpose || {},
    source: c.source,
    previous: c.previous,
  };
};

const leaveEdit = (doc) => ({ ...doc, editing: false, draft: null, saving: false, saveError: false });

const withCurrent = (doc, payload) => {
  const published = payload.published || doc.published;
  const current = payload.current ? entryMeta(payload.current) : doc.current;
  return {
    ...doc,
    // keepContent: the entry was just saved from the screen; a change made meanwhile stays.
    ...(payload.current && !payload.keepContent ? contentOf(payload.current) : {}),
    published,
    mine: payload.mine || doc.mine,
    pointer: payload.pointer === undefined ? doc.pointer : payload.pointer,
    current,
    updateAvailable: hasUpdateAvailable(payload.current || { ...current, state: { source: doc.source } }, published),
  };
};

function docReducer(doc = EMPTY_DOC, { type, payload }) {
  switch (type) {
    case ANALYTICS_STATE_LOADED:
      return {
        ...withCurrent(doc, payload),
        scope: payload.scope,
        typology: payload.typology,
        followers: null,
        publishedWriteAsked: false,
        publishedWriteAcknowledged: false,
        loaded: true,
      };
    case ANALYTICS_DASHBOARD_SWITCHED:
      return {
        ...leaveEdit(withCurrent(doc, payload)),
        followers: null,
        publishedWriteAsked: false,
        publishedWriteAcknowledged: false,
        viewEpoch: doc.viewEpoch + 1,
      };
    case ANALYTICS_DASHBOARDS_CHANGED:
      return withCurrent(doc, payload);
    case ANALYTICS_DOCUMENT_SAVED:
      // A PUT that lands after the user opened another dashboard must not stamp that one's version.
      return doc.current && doc.current.dashboardKey === payload.dashboardKey && Boolean(doc.current.published) === Boolean(payload.published)
        ? { ...doc, current: { ...doc.current, version: payload.version, updatedAt: payload.updatedAt, updatedBy: payload.updatedBy } }
        : doc;
    case ANALYTICS_FOLLOWERS_LOADED:
      return { ...doc, followers: payload };
    case ANALYTICS_PUBLISHED_WRITE_ASKED:
      return { ...doc, publishedWriteAsked: true };
    case ANALYTICS_PUBLISHED_WRITE_ACKNOWLEDGED:
      return { ...doc, publishedWriteAsked: false, publishedWriteAcknowledged: true };
    case ANALYTICS_PUBLISHED_WRITE_DECLINED:
      return { ...doc, publishedWriteAsked: false };
    case ANALYTICS_NAME_CHANGED:
      return { ...doc, name: payload };
    case ANALYTICS_FILTERS_CHANGED:
      return { ...doc, filters: payload };
    case ANALYTICS_CHART_OPTION_CHANGED:
      return {
        ...doc,
        chartOptions: {
          ...doc.chartOptions,
          [payload.panelId]: { ...doc.chartOptions[payload.panelId], [payload.key]: payload.value },
        },
      };
    case ANALYTICS_SERIES_STYLE_CHANGED:
      return { ...doc, seriesStyles: { ...doc.seriesStyles, [payload.fieldKey]: payload.style } };
    case ANALYTICS_EDIT_STARTED:
      return { ...doc, editing: true, saveError: false, draft: { layout: doc.layout, hidden: doc.hidden } };
    case ANALYTICS_DRAFT_LAYOUT_CHANGED:
      return doc.editing ? { ...doc, draft: { ...doc.draft, layout: payload } } : doc;
    case ANALYTICS_DRAFT_PANEL_TOGGLED:
      return doc.editing ? { ...doc, draft: { ...doc.draft, hidden: payload } } : doc;
    case ANALYTICS_EDIT_CANCELLED:
      return { ...leaveEdit(doc), viewEpoch: doc.viewEpoch + 1 };
    case ANALYTICS_VIEW_SAVING:
      return { ...doc, saving: true, saveError: false };
    case ANALYTICS_VIEW_SAVED: {
      const source = payload.source || doc.source;
      return {
        ...leaveEdit(doc),
        layout: payload.layout,
        hidden: payload.hidden,
        chartOptions: payload.chartOptions || doc.chartOptions,
        seriesStyles: payload.seriesStyles || doc.seriesStyles,
        filters: payload.filters ?? doc.filters,
        purpose: payload.purpose ?? doc.purpose,
        source,
        // Reset to the published version moves the copy's source forward: the badge goes with it.
        updateAvailable: hasUpdateAvailable(doc.current && { ...doc.current, state: { source } }, doc.published),
        viewEpoch: payload.relayout ? doc.viewEpoch + 1 : doc.viewEpoch,
      };
    }
    case ANALYTICS_VIEW_SAVE_FAILED:
      return { ...doc, saving: false, saveError: true };
    default:
      return doc;
  }
}

export default function portfolioAnalyticsReducer(state = initialState, action = {}) {
  const { type, payload, docKey } = action;
  if (type === ANALYTICS_SCENARIO_PROJECTS_LOADED) return { ...state, scenarioProjectMap: payload || {} };
  if (!docKey) return state;
  const before = state.docs[docKey];
  const after = docReducer(before, { type, payload });
  return after === before ? state : { ...state, docs: { ...state.docs, [docKey]: after } };
}
